import {Button, Modal, NumberInput, Select, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {zodResolver} from '@hookform/resolvers/zod';
import React, {useEffect, useMemo, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import type {
  ModuleEnum,
  ModuleExtensionFormTabRequest,
  ModuleExtensionFormTabResponse,
} from 'src/api/ProactApiClient';
import {useGetFormDefinitionListQuery} from 'src/redux/queries/formDefinition';
import {z} from 'zod';

export interface FormTabFormModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (request: ModuleExtensionFormTabRequest) => Promise<void>;
  editingTab: ModuleExtensionFormTabResponse | null;
  module?: ModuleEnum;
}

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const formTabSchema = z.object({
  formDefinitionId: z.string().min(1, 'Form definition is required'),
  tabLabel: z
    .string()
    .min(1, 'Tab label is required')
    .max(255, 'Tab label must be 255 characters or fewer'),
  tabSlug: z
    .string()
    .min(1, 'Tab slug is required')
    .max(255, 'Tab slug must be 255 characters or fewer'),
  sortOrder: z.number().min(0, 'Sort order must be 0 or greater'),
  isActive: z.boolean(),
});

type FormTabFormValues = z.infer<typeof formTabSchema>;

const emptyValues: FormTabFormValues = {
  formDefinitionId: '',
  tabLabel: '',
  tabSlug: '',
  sortOrder: 0,
  isActive: true,
};

export const FormTabFormModal: React.FC<FormTabFormModalProps> = ({
  opened,
  onClose,
  onSave,
  editingTab,
  module,
}) => {
  FormTabFormModal.displayName = 'FormTabFormModal';

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const {data: formDefinitionData} = useGetFormDefinitionListQuery(
    {start: 0, length: 10000, active: true, includeExtensionOnly: true},
    {skip: !opened}
  );

  const formDefinitions = useMemo(
    () => formDefinitionData?.data ?? [],
    [formDefinitionData]
  );

  const formDefinitionOptions = useMemo(
    () =>
      formDefinitions.map(d => ({
        value: String(d.id),
        label: d.name ?? `Definition #${d.id}`,
      })),
    [formDefinitions]
  );

  const initialValues = useMemo<FormTabFormValues>(
    () =>
      editingTab
        ? {
            formDefinitionId: String(editingTab.formDefinitionId),
            tabLabel: editingTab.tabLabel,
            tabSlug: editingTab.tabSlug,
            sortOrder: editingTab.sortOrder,
            isActive: editingTab.isActive,
          }
        : emptyValues,
    [editingTab]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: {isValid, isDirty, isSubmitting},
  } = useForm<FormTabFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(formTabSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const formDefinitionId = watch('formDefinitionId');

  const selectedFormDefinition = useMemo(
    () => formDefinitions.find(d => String(d.id) === formDefinitionId),
    [formDefinitions, formDefinitionId]
  );

  const showExtensionOnlySuggestion =
    selectedFormDefinition && !selectedFormDefinition.extensionOnly;

  const showSensitiveDataWarning =
    selectedFormDefinition?.sensitiveData === true;

  const handleLabelChange = (value: string) => {
    setValue('tabLabel', value, {shouldValidate: true, shouldDirty: true});
    if (!slugManuallyEdited) {
      setValue('tabSlug', slugify(value), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleSlugChange = (value: string) => {
    setValue('tabSlug', value, {shouldValidate: true, shouldDirty: true});
    setSlugManuallyEdited(true);
  };

  const onSubmit = async (values: FormTabFormValues) => {
    if (!module) return;
    await onSave({
      module,
      formDefinitionId: Number(values.formDefinitionId),
      tabLabel: values.tabLabel,
      tabSlug: values.tabSlug,
      sortOrder: values.sortOrder,
      isActive: values.isActive,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editingTab ? 'Edit Form Tab' : 'Create Form Tab'}
      size="md">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="sm">
          <Controller
            name="formDefinitionId"
            control={control}
            render={({field, fieldState}) => (
              <Select
                label="Form Definition"
                placeholder="Select a form definition"
                data={formDefinitionOptions}
                value={field.value || null}
                onChange={val => field.onChange(val ?? '')}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                searchable
                required
              />
            )}
          />
          {showExtensionOnlySuggestion && (
            <Alert color="blue" variant="light">
              This form is not marked as &quot;Extension Tab Only&quot;. It will
              still appear in the Forms module lists. To hide it, enable the
              &quot;Extension Tab Only&quot; toggle in the form designer
              settings.
            </Alert>
          )}
          {showSensitiveDataWarning && (
            <Alert color="yellow" variant="light">
              This form contains sensitive data. Tab permissions control tab
              visibility, but users will only see their own submissions.
            </Alert>
          )}
          <Controller
            name="tabLabel"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="Tab Label"
                placeholder="e.g., Corrective Action"
                {...field}
                onChange={e => handleLabelChange(e.currentTarget.value)}
                error={fieldState.error?.message}
                required
                maxLength={255}
              />
            )}
          />
          <Controller
            name="tabSlug"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="Tab Slug"
                placeholder="e.g., corrective-action"
                {...field}
                onChange={e => handleSlugChange(e.currentTarget.value)}
                error={fieldState.error?.message}
                required
                maxLength={255}
              />
            )}
          />
          <Controller
            name="sortOrder"
            control={control}
            render={({field, fieldState}) => (
              <NumberInput
                label="Sort Order"
                value={field.value}
                onChange={val => field.onChange(Number(val))}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                min={0}
              />
            )}
          />
          <Controller
            name="isActive"
            control={control}
            render={({field}) => (
              <Switch
                label="Active"
                checked={field.value}
                onChange={e => field.onChange(e.currentTarget.checked)}
              />
            )}
          />
        </Stack>
        <Group justify="flex-end" mt="md">
          <Button size="sm" color="black" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            loading={isSubmitting}
            disabled={!isValid || !isDirty}>
            {editingTab ? 'Save' : 'Create'}
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
